// src/components/guards/ExerciseTokensGuard.jsx
//
// Guard INLINE para ejercicios que consumen tokens IA.
// Diferencia con SubscriptionGuard:
//   - Solo bloquea cuando status = "tokens" (sin tokens este mes)
//   - Muestra el overlay DENTRO del ejercicio, no en toda la página
//   - overdue / paused → NO actúa (SubscriptionGuard de ruta ya los maneja)
//
// USAR EN: SpeakingExercise, WritingExercise (y cualquier ejercicio con IA)
// NO usar en: Quiz, Matching, Listening, Reading, Fill-in-the-blank, etc.

import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscriptionStatus } from "./SubscriptionGuard";

// ── Estilos inline para no añadir un archivo CSS extra ───────────────────────
const styles = {
  root: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  blur: {
    filter: "blur(6px)",
    pointerEvents: "none",
    userSelect: "none",
    width: "100%",
    height: "100%",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    background: "rgba(10, 10, 10, 0.55)",
    backdropFilter: "blur(2px)",
    borderRadius: "inherit",
  },
  card: {
    background: "rgba(20, 20, 20, 0.95)",
    border: "1px solid rgba(245, 166, 35, 0.35)",
    borderRadius: "16px",
    padding: "28px 32px",
    maxWidth: "380px",
    width: "90%",
    textAlign: "center",
    boxShadow: "0 0 0 1px rgba(245,166,35,.15), 0 24px 48px rgba(0,0,0,.6)",
  },
  icon: {
    fontSize: "40px",
    marginBottom: "12px",
    display: "block",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.1em",
    color: "var(--sg-wrn, #f5a623)",
    background: "rgba(245,166,35,.1)",
    border: "1px solid rgba(245,166,35,.3)",
    borderRadius: "6px",
    padding: "3px 10px",
    marginBottom: "14px",
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#f5a623",
    boxShadow: "0 0 6px #f5a623",
    flexShrink: 0,
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#ffffff",
    margin: "0 0 8px",
  },
  body: {
    fontSize: "13px",
    color: "rgba(255,255,255,0.6)",
    lineHeight: 1.6,
    margin: "0 0 20px",
  },
  divider: {
    height: "1px",
    background: "rgba(255,255,255,0.08)",
    margin: "0 0 20px",
  },
  btnPrimary: {
    display: "block",
    width: "100%",
    padding: "12px 0",
    borderRadius: "10px",
    border: "1px solid rgba(245,166,35,.5)",
    background: "rgba(245,166,35,.12)",
    color: "#f5a623",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    marginBottom: "10px",
    transition: "background .18s, box-shadow .18s",
  },
  btnSecondary: {
    display: "block",
    width: "100%",
    padding: "10px 0",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,.12)",
    background: "transparent",
    color: "rgba(255,255,255,0.45)",
    fontSize: "13px",
    cursor: "pointer",
    transition: "color .15s",
  },
  hint: {
    fontSize: "11px",
    color: "rgba(255,255,255,0.3)",
    marginTop: "12px",
  },
};

// ─────────────────────────────────────────────────────────────────────────────

function TokensOverlay() {
  const navigate  = useNavigate();
  const cardRef   = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.opacity   = "0";
    card.style.transform = "scale(0.95)";
    requestAnimationFrame(() => {
      card.style.transition = "opacity .35s ease, transform .35s ease";
      card.style.opacity    = "1";
      card.style.transform  = "scale(1)";
    });
  }, []);

  return (
    <div style={styles.overlay} role="dialog" aria-label="Tokens agotados">
      <div ref={cardRef} style={styles.card}>
        <span style={styles.icon}>⚠️</span>

        <div style={styles.badge}>
          <span style={styles.dot} />
          SIN CRÉDITOS IA
        </div>

        <h3 style={styles.title}>Tokens IA Agotados</h3>
        <p style={styles.body}>
          Este ejercicio requiere créditos IA. Has usado todos tus tokens este mes.
          Los demás ejercicios del curso siguen disponibles.
        </p>

        <div style={styles.divider} />

        <button
          style={styles.btnPrimary}
          onClick={() => navigate("/plans?tab=tokens")}
        >
          ⚡ Actualizar Plan
        </button>

        <button
          style={styles.btnSecondary}
          onClick={() => navigate(-1)}
        >
          ← Volver al curso
        </button>

        <p style={styles.hint}>Los tokens se renuevan el 1° de cada mes</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  COMPONENTE EXPORTADO
// ─────────────────────────────────────────────────────────────────────────────

export default function ExerciseTokensGuard({ children }) {
  const { status, loading } = useSubscriptionStatus();

  // Mientras carga: renderiza el ejercicio directamente (sin flicker)
  if (loading) return <>{children}</>;

  // Solo actúa en "tokens" — los demás estados ya los maneja SubscriptionGuard
  if (status !== "tokens") return <>{children}</>;

  return (
    <div style={styles.root}>
      {/* Ejercicio difuminado al fondo */}
      <div style={styles.blur} aria-hidden="true">
        {children}
      </div>

      {/* Overlay de tokens encima */}
      <TokensOverlay />
    </div>
  );
}